
import { useState } from "react";
import { useTranslations } from "use-intl";
import { useNavigate } from "@tanstack/react-router";
import {
  Play,
  Pencil,
  Power,
  GitBranch,
  Calendar,
  Clock,
  Share2,
  LockKeyhole,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { WorkflowKindChip } from "@/features/workflows/components/workflow-kind-chip";
import { CostBudgetChip } from "@/features/workflows/components/cost/cost-budget-chip";
import { FolderChip } from "@/components/ui/folder-chip/folder-chip";
import { StatusChip } from "@/components/ui/status-chip/status-chip";
import { ResourceShareDialog } from "@/components/shared/permissions/resource-share-dialog";
import { useAiConfigReadiness } from "@/features/llm/hooks/use-aiconfig-readiness";
import { LlmSetupRequiredCard } from "@/features/llm/components/llm-setup-required-card";
import { useCostBudget } from "@/hooks/api/queries/cost-budget.queries";
import type { Workflow } from "@/types/workflow.types";
import type { UseCase } from "@/features/llm/types";
import type { ActionDef } from "@/components/shared/permissions/resource-permission-matrix";

interface WorkflowHeaderProps {
  workflow: Workflow;
  onRun: () => void;
  onEdit: () => void;
  onToggleEnabled: () => void;
  isRunning?: boolean;
  isToggling?: boolean;
  runDisabledReason?: string | null;
}

const WORKFLOW_ACTIONS: ActionDef[] = [
  { key: "read", labelKey: "permissions:action.view", label: "View" },
  { key: "write", labelKey: "permissions:action.edit", label: "Edit" },
  { key: "run", labelKey: "permissions:action.run", label: "Run" },
  { key: "approve_checkpoint", labelKey: "permissions:action.approve_checkpoint", label: "Approve checkpoint" },
];

export default function WorkflowHeader({
  workflow,
  onRun,
  onEdit,
  onToggleEnabled,
  isRunning,
  isToggling,
  runDisabledReason,
}: WorkflowHeaderProps) {
  const t = useTranslations("workflows");
  const navigate = useNavigate();
  const [isShareOpen, setIsShareOpen] = useState(false);

  // Intersect workflow's required use-cases against readiness — disable Run
  // and show setup card if any required use-case is not ready.
  // requiredUseCases absent on older API responses → treat as [] (no gate).
  const { isLoading: isReadinessLoading, isReady, getReason } = useAiConfigReadiness();
  const requiredUseCases = workflow.requiredUseCases ?? [];
  const unreadyUseCases = requiredUseCases.filter((uc) => !isReady(uc as UseCase));
  const canRun = !isReadinessLoading && unreadyUseCases.length === 0 && !runDisabledReason;

  const scheduleIcon =
    workflow.scheduleType === "cron"
      ? Calendar
      : workflow.scheduleType === "once"
        ? Clock
        : Play;
  const ScheduleIcon = scheduleIcon;

  // Today's workflow-level cost for header chip.
  const { data: budget } = useCostBudget({
    resourceType: "workflow",
    resourceId: workflow.id,
  });

  // Folder display — workflow.folderId + workflow.folderName if available.
  const folderName = (workflow as Workflow & { folderName?: string }).folderName;
  const folderId = (workflow as Workflow & { folderId?: string }).folderId;

  const actions = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <span title={runDisabledReason ?? undefined}>
        <Button
          variant="primary"
          size="md"
          onClick={onRun}
          disabled={isRunning || !canRun}
          startIcon={<Play className="size-4" aria-hidden />}
        >
          {isRunning ? t("header.running") : t("header.run")}
        </Button>
      </span>
      <Button
        variant="outline"
        size="md"
        onClick={onEdit}
        aria-label={t("header.edit")}
        startIcon={<Pencil className="size-4" aria-hidden />}
      >
        <span className="hidden sm:inline">{t("header.edit")}</span>
      </Button>
      <Button
        variant="outline"
        size="md"
        onClick={onToggleEnabled}
        disabled={isToggling}
        aria-label={workflow.isEnabled ? t("header.disable") : t("header.enable")}
        startIcon={<Power className="size-4" aria-hidden />}
      >
        <span className="hidden sm:inline">
          {workflow.isEnabled ? t("header.disable") : t("header.enable")}
        </span>
      </Button>
      <Button
        variant="outline"
        size="md"
        onClick={() => setIsShareOpen(true)}
        aria-label={t("permissions.share_button_aria")}
        startIcon={<Share2 className="size-4" aria-hidden />}
      >
        <span className="hidden sm:inline">{t("permissions.share_btn")}</span>
      </Button>
    </div>
  );

  return (
    <div className="space-y-3">
      <PageHeader
        icon={GitBranch}
        title={workflow.name}
        description={workflow.description}
        badge={workflow.kind ? <WorkflowKindChip kind={workflow.kind} size="sm" /> : undefined}
        actions={actions}
      >
        <div className="flex flex-wrap items-center gap-2">
          {folderName && folderId && (
            <FolderChip
              path={folderName}
              onClick={() => navigate({ to: "/workflows", search: { folderId } })}
              clickAriaLabel={t("header.folder_nav_aria", { name: folderName })}
            />
          )}
          <span className="inline-flex max-w-full items-center rounded-full border border-[var(--border)] bg-[var(--card)] px-2 py-0.5 font-mono text-xs text-[var(--muted-foreground)]">
            <span className="max-w-[360px] truncate">{workflow.slug}</span>
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-[var(--border)] bg-[var(--card)] px-2 py-0.5 text-xs font-medium text-[var(--muted-foreground)]">
            <ScheduleIcon className="size-3.5" aria-hidden />
            <span className="capitalize">{workflow.scheduleType}</span>
          </span>
          <StatusChip tone={workflow.isEnabled ? "done" : "queued"}>
            {workflow.isEnabled ? t("header.enabled") : t("header.disabled")}
          </StatusChip>
          {budget && (budget.currentUsd > 0 || budget.capUsd != null) && (
            <CostBudgetChip
              currentUsd={budget.currentUsd}
              capUsd={budget.capUsd}
              mode="workflow"
            />
          )}
        </div>

        {!isReadinessLoading && unreadyUseCases.length > 0 && (
          <div className="mt-3 max-w-xl">
            <LlmSetupRequiredCard
              useCase={unreadyUseCases[0] as UseCase}
              reason={getReason(unreadyUseCases[0] as UseCase)}
            />
          </div>
        )}

        {runDisabledReason && (
          <div className="mt-3 flex max-w-2xl items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
            <LockKeyhole className="mt-0.5 size-4 shrink-0" aria-hidden />
            <div className="min-w-0">
              <p className="text-sm font-semibold">{t("agentic_runtime.disabled_title")}</p>
              <p className="mt-0.5 text-xs leading-5">{t("agentic_runtime.existing_desc")}</p>
            </div>
          </div>
        )}
      </PageHeader>

      {/* Share dialog — entry point from header for quick sharing */}
      <ResourceShareDialog
        resourceType="workflow"
        resourceId={workflow.id}
        actions={WORKFLOW_ACTIONS}
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        title={t("permissions.dialog_title", { name: workflow.name })}
      />
    </div>
  );
}
